// FE Dome Model — application state and per-frame Update pipeline.
//
// State fields are intentionally flat so the UI layer can bind directly to
// them. Every mutation goes through setState() which triggers a recompute
// and then emits an 'update' event for the renderer / panel to react.

import { Clamp, Limit01, Limit1, ToRad } from '../math/utils.js';
import { traceDomeCaustic } from '../render/domeCaustic.js';
import { V } from '../math/vect3.js';
import { M } from '../math/mat3.js';
import { CELESTIAL, GEOMETRY, FE_RADIUS, TIME_ORIGIN, initTimeOrigin } from './constants.js';
import { dateTimeToDate } from './time.js';
import {
  sunEquatorial, moonEquatorial, greenwichSiderealDeg, equatorialToCelestCoord,
  planetEquatorial, PLANET_NAMES, bodyRADec, BODY_NAMES, bodyParallaxSine,
  bodyGeocentric, geo as ephGeo, ptol as ephPtol,
  apix as ephApix, vsop as ephVsop,
} from './ephemeris.js';
import { apparentStarPosition, findSolarEclipseContactWindow, refineEclipseByMinSeparation } from '../ephem/common.js';
import { besselianShadowPathFromElements } from './besselianEclipse.js';
import { ECLIPSE_BESSELIAN } from '../data/eclipseBesselian.js';
import { CEL_NAV_STARS, celNavStarById } from './celnavStars.js';
import { CATALOGUED_STARS, cataloguedStarById } from './constellations.js';
import { BLACK_HOLES, blackHoleById } from './blackHoles.js';
import { QUASARS,      quasarById }    from './quasars.js';
import { GALAXIES,     galaxyById }    from './galaxies.js';
import { CEL_THEO_STARS, CEL_THEO_OWN } from './celTheoStars.js';
import { SATELLITES,   satelliteById, satelliteSubPoint } from './satellites.js';
import { SATELLITES_EXTRA } from './satellitesExtra.js';
import {
  compTransMatCelestToSky, compTransMatLocalFeToGlobalFe, compTransMatVaultToFe,
  celestCoordToLocalSkyCoord, coordToLatLong, localSkyCoordToAngles,
  localSkyCoordToGlobalFeCoord, vaultCoordToGlobalFeCoord,
} from './transforms.js';
import {
  feLatLongToGlobalFeCoord, celestLatLongToVaultCoord, vaultCoordAt,
} from './feGeometry.js';
import { canonicalLatLongToDisc } from './canonical.js';
import { applyRefractionLocalSky, refractionDeg } from './refraction.js';
import { parallaxAltitudeLocalSky } from './topocentric.js';
import { findNearestSolarEclipse } from './solarEclipseSchedule.js';
import { computeSolarEclipseShadowPath } from './besselianEclipse.js';
// FE and GE geometry interpreters. Both consume the shared Tang-canonical
// celestial sphere; neither imports the other. See js/core/interpret/.
import * as feInterp from './interpret/fe.js';
import * as geInterp from './interpret/ge.js';
// Terrestrial Chinese coordinate frame: observer lat/long is canonically a
// Chinese du ground record, decoded to modern degrees for the pipeline.
import { groundDegToTang, groundTangToDeg } from '../tang/ground.js';

// FE optical-cap primitives now live in the FE interpreter; alias the names
// so the per-body call sites below stay unchanged.
const opticalVaultProject  = feInterp.opticalVaultProject;
const heavenlyVaultCeiling = feInterp.heavenlyVaultCeiling;

// Mirrors PLANET_STYLE colours in render/index.js and the Tracker
// button grid. Keep in sync.
const PLANET_GP_COLORS = {
  mercury: 0xd0b090,
  venus:   0xfff0c8,
  mars:    0xd05040,
  jupiter: 0xffa060,
  saturn:  0xe4c888,
  uranus:  0xa8d8e0,
  neptune: 0x7fa6e8,
};
const TRACKED_GP_COLORS_PLANET_DEFAULT = 0xff8c66;

// Keys that don't drive any computed value inside `App.update` —
// they're text overlays, HUD readouts, or panel collapse flags
// consumed only by the renderers / control panel. `setState`
// short-circuits the heavy recompute when a patch only touches
// these. Any new key that *would* affect a `c.*` value MUST stay
// out of this set.
const _UI_ONLY_STATE_KEYS = new Set([
  'Description',
  'MouseElevation',
  'MouseAzimuth',
  'ClearTraceCount',
  'MoonPhaseExpanded',
  'ShowLiveEphemeris',
  // Pure display toggle: switches the Observer angular rows between degrees
  // and du. Doesn't touch any computed value.
  'ObserverUnitsChinese',
]);

// Default state. Distances in FE_RADIUS units.
// Golden-section search over VaultHeight that lands the antipodal
// caustic peak at the same observer-local elevation as the real sun.
// Returns the best dome height in [0.05, 0.7] or NaN if no peak is
// ever above the horizon.
function _fitDomeVaultHeight(sunPos, domeR, obs, targetElDeg) {
  const elevForH = (domeH) => {
    const r = traceDomeCaustic({
      sunPos, domeR, domeH,
      discClipR: FE_RADIUS * 1.4,
      nTheta: 240, nPhi: 120,
    });
    let bestEl = null;
    let bestScore = -Infinity;
    for (const p of (r.peaks || [])) {
      const r2 = (p.x * p.x + p.y * p.y) / (domeR * domeR);
      const peakZ = r2 < 1 ? domeH * Math.sqrt(1 - r2) : 0;
      const dx = p.x - obs[0];
      const dy = p.y - obs[1];
      const dz = peakZ - obs[2];
      const dlen = Math.sqrt(dx * dx + dy * dy + dz * dz);
      if (dlen < 1e-9 || dz <= 0) continue;
      // Prefer the strongest above-horizon peak.
      const el = Math.asin(dz / dlen) * 180 / Math.PI;
      const score = p.intensity;
      if (score > bestScore) { bestScore = score; bestEl = el; }
    }
    return bestEl;
  };
  const loss = (h) => {
    const e = elevForH(h);
    return e == null ? Infinity : Math.abs(e - targetElDeg);
  };
  let lo = 0.05, hi = 0.7;
  const phi = (Math.sqrt(5) - 1) / 2;
  let h1 = hi - phi * (hi - lo);
  let h2 = lo + phi * (hi - lo);
  let l1 = loss(h1), l2 = loss(h2);
  for (let i = 0; i < 20; i++) {
    if (l1 < l2) { hi = h2; h2 = h1; l2 = l1; h1 = hi - phi * (hi - lo); l1 = loss(h1); }
    else         { lo = h1; h1 = h2; l1 = l2; h2 = lo + phi * (hi - lo); l2 = loss(h2); }
    if (Math.abs(hi - lo) < 0.005) break;
  }
  const best = (l1 < l2) ? h1 : h2;
  return isFinite(loss(best)) ? best : NaN;
}

// Default DateTime = days since 2017-01-01 at the moment the page
// loads, so the app opens on the user's current real-world date —
// well inside DE405's 2019–2030 coverage instead of the old hard-
// coded 2019-03-25 placeholder.
function _todayDateTime() {
  const ZERO = Date.UTC(2017, 0, 1, 0, 0, 0, 0);
  return (Date.now() - ZERO) / 86_400_000;
}

function defaultState() {
  const _now = _todayDateTime();
  const _doy = Math.floor(_now);
  const _hr  = (_now - _doy) * 24;
  return {
    ObserverLat:  32.0,
    ObserverLong: -100.8387,
    // 0 = N, 90 = E, 180 = S, 270 = W.
    ObserverHeading: 357.3098,
    ObserverElevation: 0,
    CameraDirection: -106.6,
    CameraHeight:    15.2,
    CameraDistance:  GEOMETRY.CameraDistanceDefault,
    Zoom:             4.67,
    // Optical-only. fov = 75° / OpticalZoom.
    OpticalZoom:      1.0,

    // Days since 2017-01-01. Computed at load so the app opens on
    // the current real-world UTC date (inside DE405's 2019–2030
    // window for now; fallback chain handles dates beyond it).
    DateTime:    _now,
    DayOfYear:   _doy,
    Time:        _hr,

    VaultSize:   GEOMETRY.VaultSizeDefault,
    VaultHeight: 0.75,

    // H = R → hemispheric Optical projection. Reducing H only affects
    // the Heavenly cap depiction (see OpticalVaultHeightEffective).
    OpticalVaultSize:   GEOMETRY.OpticalVaultRadiusFar,
    OpticalVaultHeight: 0.5,

    RayParameter: 2.0,
    RayTarget:    0,
    RaySource:    0,

    ShowFeGrid:     false,
    ShowShadow:     true,
    ShowVault:      false,
    ShowVaultGrid:   false,
    ShowTruePositions: false,
    ShowSunTrack:   false,
    ShowMoonTrack:  false,
    ShowOpticalVault:     true,
    ShowStars:      true,
    ShowVaultRays:        false,
    ShowOpticalVaultRays: false,
    ShowManyRays:   false,
    ShowProjectionRays: false,
    // Tropics split into three independent toggles (legacy
    // `ShowTropics` flag retired; old URL params will silently fall
    // through to the per-line defaults below).
    ShowTropicCancer:     false,
    ShowEquator:          false,
    ShowTropicCapricorn:  false,
    ShowPolarCircles:     false,
    ShowGroundPoints:  false,
    ShowFacingVector:  false,
    ShowDecCircles:    false,
    ShowLogo:          true,
    ShowTooltips:      true,
    ShowConstellations:      true,
    ShowConstellationLines:  true,
    ShowLongitudeRing:       false,
    ShowAzimuthRing:         false,
    ShowOpticalVaultGrid:    false,
    ShowCelestialPoles:      false,
    DarkBackground:          true,
    ShowLiveEphemeris:       false,
    MoonPhaseExpanded:       false,
    ShowSatellites:          false,
    ShowAxisLine:            false,
    LastObserverLat:         null,
    LastObserverLong:        null,
    ObserverAtCenter:        false,
    // Flight Routes demo overlay. Selected = 'all' | route id | array
    // of ids; null collapses to 'all' when ShowFlightRoutes is on.
    // Progress (0..1) clips each route partway along its arc so the
    // demo animator can sweep planes from departure to arrival.
    ShowFlightRoutes:        false,
    FlightRoutesSelected:    'all',
    FlightRoutesProgress:    1,
    // Per-demo override that suppresses the cyan endpoint→origin
    // central-angle legs even when a route is selected. Used by the
    // Equal Arc demos so the side-by-side race lanes own the screen
    // without the centre-pointing legs cluttering the disc / globe.
    HideFlightCentralAngle:  false,
    // Per-route colour override map: { [routeId]: '#hex' }. Renderer
    // reads this when colouring the route line, complement dashed
    // line, ring marker, leader line, and plane texture. Routes not
    // listed fall back to the default orange.
    FlightRouteColors:       {},
    // HUD overlay populated by flight-routes demos. `null` = hide.
    // Otherwise `{ title, lines: string[] }`.
    FlightInfoBox:           null,
    // Side panel showing two routes "straightened to scale" so the
    // user can watch two planes race along straight horizontal
    // tracks. `null` = hide; otherwise
    // `{ title, lanes: [{ label, angle, color }, ...] }`. Track
    // pixel-length scales with the per-lane `angle` so equal central
    // angles draw equal-length straight lines.
    FlightRaceTrack:         null,
    ShowGPPath:              false,
    GPPathDays:              1,
    // GE-only inscribed/central-angle helpers. ShowCentralAngle
    // draws a great-circle arc from the observer's surface point
    // to the tracked body's GP; ShowInscribedAngle drops a short
    // dashed tick perpendicular to that arc at its midpoint
    // (which sits at central/2 from each endpoint by the
    // inscribed-angle theorem).
    ShowCentralAngle:        false,
    ShowInscribedAngle:      false,
    ShowStellariumOverlay:   false,
    // 2024-04-08 total solar eclipse Besselian-axis path overlay
    // (red polyline + greatest-eclipse marker). Driven by the demo
    // intro; stays off by default.
    ShowBesselianEclipsePath: false,
    // 0..1 sweep along the eclipse path. 0 = no shadow yet (first
    // contact in the Pacific). 1 = full path drawn (last contact in
    // the North Atlantic). Animated by the demo's Trepeat / Tval
    // loop so the magnitude bands and central line build up the
    // way the real shadow sweeps across Earth.
    BesselianEclipseProgress: 1,
    // State-driven solar-eclipse path overlay. Each solar-eclipse
    // demo's intro fills these in (sublunar samples + anchor +
    // half-window) so the renderer can sweep the shadow in
    // lockstep with the demo's DateTime tween. Null defaults
    // mean "no eclipse path active" and the renderer skips this
    // pathway entirely.
    EclipseShadowPath: null,
    EclipseShadowAnchorDt: null,
    EclipseShadowHalfWindowDays: null,
    EclipseShadowL1: null,
    EclipseShadowL2: null,
    ShowEclipseShadowPath: false,
    // Free-play live-eclipse detector. When ON (default), the
    // simulator's `update()` watches `DateTime` for a ±2 h window
    // around any 2021–2040 Espenak solar eclipse and stuffs the
    // sublunar shadow path into `computed.LiveEclipseShadowPath`
    // so `BesselianEclipsePath` paints the umbra + penumbra bands
    // automatically as each event passes. Toggle off to silence
    // the overlay.
    LiveEclipseShadows: true,
    // When ON, every visible latitude ring (Tropic of Cancer /
    // Equator / Tropic of Capricorn / Polar Circles) gets a
    // secondary label showing its dimensions in li — distance
    // from the N-pole-centred AE disc origin, plus the ring
    // circumference on the underlying Tang sphere. Off by
    // default so the ring labels stay clean for users who don't
    // care about Chinese-unit annotations.
    ShowLatLineInfo: false,
    // Tang-sphere dimension overlay: dashed vertical from N
    // pole to dome apex (height = R_LI), dashed radial to the
    // ground rim (radius = R_LI), and the dome diameter (= 2 R_LI)
    // labeled in li. Off by default; toggled from View → Show.
    ShowTangSphereDims: false,
    // Cursor for the "skip to next solar eclipse" button next to
    // the Cel-Theo PP shortcut. Each click advances DateTime to
    // the next eclipse anchor (minus a small lead-in) and bumps
    // this index — wraps to the first eclipse past the catalogue
    // end. Re-zeroed when the user manually rewinds the clock.
    SkipEclipseIndex: 0,
    ShowSunMoonNine:         false,
    ShowGPTracer:            false,
    ShowOpticalVaultTrace:   false,
    ShowTraceUnder:          false,
    TraceCelestialFrame:     false,
    ClearTraceCount:         0,
    GPTracerTargets:         [],
    SunMonthMarkers:         [],
    SunVaultArcOn:           false,
    SunMonthMarkersWorldSpace: false,
    SunMonthMarkersOpp:      [],
    SunMonthMarkersOppWorldSpace: false,
    EclipseMapSolar:         [],
    EclipseMapLunar:         [],
    WorldModel:              'fe',
    ShowDomeCaustic:         false,
    DomeCausticDensity:      120,
    MoonMonthMarkers:        [],
    MoonVaultArcOn:          false,
    MoonMonthMarkersWorldSpace: false,
    ShowSunAnalemma:         false,
    ShowMoonAnalemma:        false,
    ShowCelestialBodies:     true,
    ShowCelNav:              true,
    ShowBlackHoles:          true,
    ShowQuasars:             true,
    ShowGalaxies:            true,
    ShowCelTheo:             true,
    Language:                'en',
    GPOverridePlanets:         false,
    GPOverrideCelNav:          false,
    GPOverrideConstellations:  false,
    GPOverrideBlackHoles:      false,
    GPOverrideQuasars:         false,
    GPOverrideGalaxies:        false,
    GPOverrideSatellites:      false,

    InsideVault: false,

    ObserverFigure: 'nikki',

    // Observer angular rows (Lat / Long / Elevation / Azi) display in modern
    // degrees by default; the Observer "Chinese du units" checkbox flips the
    // readout + input to du. Display-only; stored values stay in degrees.
    ObserverUnitsChinese: false,

    // Minutes east of UTC. -360 = CST.
    TimezoneOffsetMinutes: -360,
    StarfieldVaultHeight: 0.485,
    MoonVaultHeight:      0.545,
    SunVaultHeight:       0.545,
    MercuryVaultHeight:   0.545,
    VenusVaultHeight:     0.545,
    MarsVaultHeight:      0.545,
    JupiterVaultHeight:   0.545,
    SaturnVaultHeight:    0.545,
    UranusVaultHeight:    0.545,
    NeptuneVaultHeight:   0.545,

    ShowPlanets: true,

    // When true, starfield fades with sun elevation.
    DynamicStars: false,

    // 'none' | 'yggdrasil' | 'meru' | 'vortex' | 'vortex2' | 'discworld'
    Cosmology: 'none',

    // 'ae' | 'hellerick' | 'blank'. Affects only the map art; physics
    // always runs in the AE frame.
    MapProjection: 'ae',
    MapProjectionGe: 'hq_equirect_night',
    // Geomagnetic overlay (WMM2025 / IGRF-14).
    MagneticOverlay: 'off',     // 'off' | 'declination' | 'intensity'
    MagneticModel: 'wmm',       // 'wmm' | 'igrf'
    MagneticStyle: 'both',      // 'lines' | 'bands' | 'both'

    // 'random' | 'chart-dark' | 'chart-light' | 'celnav'
    StarfieldType: 'celnav',

    // Active ephemeris source. 'tangMaster' (full sky, modern) is active;
    // 'tangPtolz' (pure Almagest, no new bodies) is also registered. Every
    // body position funnels through the Tang canonical frame. See registry.js.
    BodySource: 'tangMaster',

    // StarTrepidation master forces all three on when true.
    StarApplyPrecession: false,
    StarApplyNutation:   false,
    StarApplyAberration: false,
    StarTrepidation:     true,

    // 'off' | 'bennett' | 'seidelman'. Lifts the optical-vault
    // position of every body by the apparent-altitude refraction
    // value of the chosen formula. True positions remain unrefracted.
    Refraction: 'off',
    // When true (and `Refraction` is on), every tracked body gets a
    // cyan ghost marker drawn at its unrefracted optical-vault
    // position so the angular gap with the apparent (refracted)
    // marker is visible.
    ShowGeocentricPosition: false,
    // Atmospheric inputs the refraction formulas pull from. Defaults
    // are MSL ICAO standard (1013.25 mbar, 15°C); both formulas'
    // adjustment factor evaluates to ~0.986 at those values.
    RefractionPressureMbar: 1013.25,
    RefractionTemperatureC: 15,
    // When true, every angular readout in the tracker HUD and
    // tracking-info popup is followed by its Chinese-units
    // equivalent — degrees ↔ du / fen via 360/365.25, and the
    // `Central` row also gets the Yi Xing 351.267 li/du calibration
    // for a li / bu ground-distance readout. DMS stays where it is;
    // the du / fen runs alongside it so the two can be read
    // together.
    ShowChineseDu: true,
    // Compass-mode distance picker. When on, map clicks drop a
    // From / To pair instead of routing through the normal body-
    // pick handler; the pair drives a marker + connector renderer
    // and a li / bu distance readout. Tracking is suppressed in
    // this mode by the mouseHandler.
    DistanceCompassMode:   false,
    DistancePairFromLat:   null,
    DistancePairFromLon:   null,
    DistancePairToLat:     null,
    DistancePairToLon:     null,
    // Manual lat/lon inputs for the View → Distance Calc panel.
    // P1 lat/lon auto-fill from the observer position the first time
    // the panel renders with all four still null; thereafter the
    // inputs hold whatever the user typed.
    DistCalcLat1: null,
    DistCalcLon1: null,
    DistCalcLat2: null,
    DistCalcLon2: null,
    // Trace toggles for the Distance Calc panel. Central angle
    // draws the minor great-circle arc P1↔P2 in cyan; inscribed
    // angle draws the same great circle's other half (Q → P1
    // and Q → P2 where Q is the antipode of the minor-arc
    // midpoint) plus a pin at Q so the inscribed-angle theorem
    // (= central/2) is geometrically visible.
    DistCalcTraceCentralAngle:   false,
    DistCalcTraceInscribedAngle: false,
    // Active Cel-Theo preset code (e.g. 'PP') or null. Drives the
    // toggle behaviour of the preset buttons — clicking an active
    // preset deactivates it and reverts the atmosphere knobs.
    CelTheoPresetActive: null,

    // Eclipse demo state hooks. Registry sets these via intro().
    EclipseActive:     false,
    EclipseKind:       null,
    EclipseEventUTMS:  null,
    EclipsePipeline:   null,
    EclipseMinSepDeg:  null,
    EclipseMagnitude:  null,
    EclipseEventType:  null,
    EclipseSunRadiusFE:      null,
    EclipseMoonRadiusFE:     null,
    // Eclipse ground-shadow feature gate (disabled pending rework).
    ShowEclipseShadow:       false,
    // GE sphere day/night terminator + optical-vault sky-cap shading.
    // Both default ON. These flags must exist in defaultState so the
    // demos snapshot includes them — otherwise a flight-routes intro
    // that flips them off has no key to restore on demo-stop, leaving
    // the user with a permanently lit globe + flat sky.
    ShowDayNightShadow:      true,
    ShowDayNightSky:         true,
    // Deprecated circular-decal overrides, kept for URL back-compat.
    EclipseUmbraRadiusFE:    null,
    EclipsePenumbraRadiusFE: null,

    // Pins NightFactor = 1.0.
    PermanentNight: false,

    // Ids: 'sun' / 'moon' / planet name / 'star:<id>'. Empty = HUD collapsed.
    TrackerTargets: [
      'sun', 'moon',
      'mercury', 'venus', 'mars', 'jupiter',
      'saturn', 'uranus', 'neptune',
      ...CEL_NAV_STARS.map((x) => `star:${x.id}`),
      ...CATALOGUED_STARS.map((x) => `star:${x.id}`),
      ...BLACK_HOLES.map((x) => `star:${x.id}`),
      ...QUASARS.map((x) => `star:${x.id}`),
      ...GALAXIES.map((x) => `star:${x.id}`),
      // Cel Theo: only the entries with their own coordinates. The
      // `extId` aliases (Regulus / Rigel / Mintaka / Alnilam /
      // Alnitak / Baten Kaitos / Deneb Algedi) already sit in the
      // default tracker via CEL_NAV_STARS / CATALOGUED_STARS /
      // NAMED_STARS_HYG, so duplicating them here would just stack
      // extra rows in the HUD pointing at the same id.
      ...CEL_THEO_OWN.map((x) => `star:${x.id}`),
    ],

    ShowEphemerisReadings: false,

    // When true, only tracked bodies + their GPs render.
    SpecifiedTrackerMode: false,

    // When true, tracker GP dots/lines render for every tracked
    // target regardless of the master `ShowGroundPoints` toggle.
    TrackerGPOverride: false,

    Description: '',
    PointerFrom: [0, 0],
    PointerTo:   [0, 0],
    PointerText: '',

    // Degrees; set by mouseHandler, null in Heavenly / off-canvas.
    MouseElevation: null,
    MouseAzimuth:   null,

    FollowTarget:   null,
    FreeCamActive:  false,
    FreeCameraMode: false,
  };
}

export class FeModel extends EventTarget {
  constructor() {
    super();
    initTimeOrigin();

    this.state = defaultState();

    // Written by update(), read by renderer.
    this.computed = {
      TransMatSkyRot:            M.Unit(),
      TransMatCelestToSky:     M.Unit(),
      TransMatLocalFeToGlobalFe: M.Unit(),
      TransMatVaultToFe:         M.Unit(),

      SkyRotAngle:     0,
      SunCelestAngle:  0,
      MoonCelestAngle: 0,
      SunRA:  0, SunDec:  0,
      MoonRA: 0, MoonDec: 0,

      SunCelestCoord:        [0, 0, 0],
      SunCelestLatLong:      { lat: 0, lng: 0 },
      SunAnglesGlobe:        { azimuth: 0, elevation: 0 },
      SunVaultCoord:          [0, 0, 0],
      SunLocalSkyCoord:    [0, 0, 0],
      SunOpticalVaultCoord:      [0, 0, 0],

      MoonCelestCoord:       [0, 0, 0],
      MoonNorthCelestCoord:  [0, 0, 0],
      MoonCelestLatLong:     { lat: 0, lng: 0 },
      MoonAnglesGlobe:       { azimuth: 0, elevation: 0 },
      MoonVaultCoord:         [0, 0, 0],
      MoonLocalSkyCoord:   [0, 0, 0],
      MoonOpticalVaultCoord:     [0, 0, 0],

      MoonPhase:             0,   // 0=new, PI=full
      MoonPhaseFraction:     0,   // 0..1 illuminated fraction
      MoonRotation:          0,

      // 0 = day, 1 = full night
      NightFactor: 0,

      ObserverFeCoord: [0, 0, 0],

      // Observer position in the canonical Chinese ground frame
      // (polar-altitude du + longitude du). Set each frame by update();
      // the pipeline degrees are decoded from it.
      ObserverGround: { polarAltDu: 0, lonDu: 0 },

      OpticalVaultRadius: GEOMETRY.OpticalVaultRadiusFar,
      OpticalVaultHeight: GEOMETRY.OpticalVaultHeightFar,

      Planets: {},
    };

    this._dayOfYearLast = this.state.DayOfYear;
    this._timeLast = this.state.Time;
    this._dateTimeLast = this.state.DateTime;
  }

  setState(patch, emit = true) {
    Object.assign(this.state, patch);
    // Skip the heavy `update()` recompute when the patch only carries
    // UI-only keys — text overlays, HUD readouts, panel collapse
    // flags. None of those keys are read inside `update()`, so the
    // computed snapshot stays valid from the last frame and the
    // renderer's `update`-event listeners can pull state directly.
    // Empty patches (`setState({})` as a "force-emit" trigger) still
    // run the recompute by design.
    const keys = patch ? Object.keys(patch) : [];
    const skipRecompute = keys.length > 0 && keys.every((k) => _UI_ONLY_STATE_KEYS.has(k));
    if (!skipRecompute) this.update();
    if (emit) this.dispatchEvent(new CustomEvent('update', { detail: this }));
  }

  resetDescription() {
    this.state.Description = '';
    this.state.PointerFrom = [0, 0];
    this.state.PointerTo   = [0, 0];
    this.state.PointerText = '';
  }

  update() {
    const s = this.state;
    const c = this.computed;

    // clamp
    s.ObserverLat  = Clamp(s.ObserverLat, -90, 90);
    s.ObserverElevation = Clamp(s.ObserverElevation, 0, 0.5);
    s.ObserverLong = ((s.ObserverLong + 180) % 360 + 360) % 360 - 180;

    // Terrestrial Chinese frame. The observer position is canonically a
    // Chinese ground record (polar-altitude du + longitude du); the degrees
    // the geometry pipeline reads are DECODED from it. Mirrors the celestial
    // Tang funnel (ephemeris -> du/xiu -> degrees). Lossless to floating
    // point, so geometry is unchanged; c.ObserverGround feeds the HUD.
    c.ObserverGround = groundDegToTang(s.ObserverLat, s.ObserverLong);
    {
      const _obsDeg = groundTangToDeg(c.ObserverGround);
      s.ObserverLat  = _obsDeg.latDeg;
      s.ObserverLong = _obsDeg.lonDeg;
    }
    s.CameraHeight = Clamp(s.CameraHeight, s.WorldModel === 'ge' ? -89.9 : -30, 89.9);
    s.CameraDirection = ((s.CameraDirection + 180) % 360 + 360) % 360 - 180;
    s.ObserverHeading = ((s.ObserverHeading % 360) + 360) % 360;
    s.Zoom         = Clamp(s.Zoom, 0.1, 10);
    s.OpticalZoom  = Clamp(s.OpticalZoom, 0.2, 75);
    s.VaultSize     = Clamp(s.VaultSize, GEOMETRY.VaultSizeMin, GEOMETRY.VaultSizeMax);
    // VaultHeight floor derives from StarfieldVaultHeight + body
    // band (HEADROOM 0.06 + SUN_RANGE 0.20 = 0.26).
    const _vhFloor = Math.max(GEOMETRY.VaultHeightMin, s.StarfieldVaultHeight + 0.26);
    s.VaultHeight   = Clamp(s.VaultHeight, _vhFloor, GEOMETRY.VaultHeightMax);
    s.OpticalVaultSize   = Clamp(s.OpticalVaultSize,
                                 GEOMETRY.OpticalVaultSizeMin, GEOMETRY.OpticalVaultSizeMax);
    s.OpticalVaultHeight = Clamp(s.OpticalVaultHeight,
                                 GEOMETRY.OpticalVaultHeightMin, GEOMETRY.OpticalVaultHeightMax);
    s.RayParameter = Clamp(s.RayParameter, 0.5, 2.0);

    const camDistMin = GEOMETRY.CameraDistanceMinRel * s.VaultSize * FE_RADIUS;
    if (s.CameraDistance < camDistMin) s.CameraDistance = camDistMin;

    // World-model switch invalidates accumulated markers — they
    // were captured in the previous projection's coords and would
    // render at wrong positions otherwise. The arc / analemma
    // accumulators auto-reset via their own keys; markers (state
    // arrays) need explicit clearing.
    if (this._lastWorldModel !== undefined && this._lastWorldModel !== s.WorldModel) {
      s.SunMonthMarkers = [];
      s.MoonMonthMarkers = [];
      s.SunMonthMarkersOpp = [];
      s.EclipseMapSolar = [];
      s.EclipseMapLunar = [];
      // ObserverAtCenter is GE-only — collapse it on any mode flip
      // so an FE switch doesn't inherit a stale "at-centre" flag.
      // ObserverLat / ObserverLong are intentionally preserved
      // across the toggle so the user's location persists; the
      // orange origin-dot click is the explicit way to teleport to
      // (90°, 0°).
      if (s.ObserverAtCenter && !s.ShowFlightRoutes) {
        s.ObserverAtCenter = false;
      }
      // Flight-routes demo lives on the southern hemisphere. The FE
      // top-down view sits over the AE pole (north up); the GE
      // top-down equivalent has to look from below the south pole or
      // the cities all hide behind the globe. Auto-flip camera
      // pitch on any mode toggle while a flight-routes demo is
      // active so the manual FE / GE button in the bottom bar keeps
      // both projections framed. Also drop the observer to the
      // globe centre on a GE switch so the central-angle legs read
      // "from each endpoint to the observer at the centre".
      if (s.ShowFlightRoutes) {
        s.CameraHeight = (s.WorldModel === 'ge') ? -89.9 : 89.9;
        s.ObserverAtCenter = (s.WorldModel === 'ge');
      }
    }
    this._lastWorldModel = s.WorldModel;

    // BodySource transition: when the user picks a pipeline that
    // doesn't cover certain planets (VSOP87 / GeoC / HelioC /
    // Ptolemy = no Uranus / Neptune; only DE405 has them), drop
    // those bodies from `TrackerTargets` so they stop appearing in
    // the tracker HUD + per-frame compute. Switching back to
    // DE405 doesn't auto-restore them — the user picks them back
    // explicitly.
    if (this._lastBodySource !== undefined && this._lastBodySource !== s.BodySource) {
      const src = (s.BodySource === 'heliocentric') ? 'heliocentric' : (s.BodySource || 'astropixels');
      const supported =
        src === 'astropixels'  ? ephApix.SUPPORTED_BODIES :
        src === 'vsop87'       ? ephVsop.SUPPORTED_BODIES :
        src === 'ptolemy'      ? ephPtol.SUPPORTED_BODIES :
        src === 'heliocentric' ? new Set(['sun','moon','mercury','venus','mars','jupiter','saturn']) :
                                 ephGeo.SUPPORTED_BODIES;
      const arr = Array.isArray(s.TrackerTargets) ? s.TrackerTargets : [];
      const pruned = arr.filter((t) => {
        if (t === 'sun' || t === 'moon') return supported.has(t);
        if (PLANET_NAMES.includes(t))    return supported.has(t);
        return true;
      });
      if (pruned.length !== arr.length) s.TrackerTargets = pruned;
      if (s.FollowTarget && (s.FollowTarget === 'sun' || s.FollowTarget === 'moon' || PLANET_NAMES.includes(s.FollowTarget))) {
        if (!supported.has(s.FollowTarget)) s.FollowTarget = null;
      }
    }
    this._lastBodySource = s.BodySource;

    // (Removed Optical → Heavenly auto-snap — observer position
    // now persists across InsideVault toggles. Manual teleport
    // via the orange origin-dot click stays available.)

    // While at the fictitious observer position, keep LastObserver*
    // in sync with the live ObserverLat / ObserverLong sliders so
    // the orange anchor dot tracks any adjustments and a projection
    // toggle pulls the up-to-date surface position. Applies to
    // both FE and GE — the fictitious observer is at the AE pole
    // / globe centre respectively.
    if (s.ObserverAtCenter) {
      s.LastObserverLat = s.ObserverLat;
      s.LastObserverLong = s.ObserverLong;
    }

    // date/time sync
    s.DayOfYear = Math.round(s.DayOfYear);
    if (s.DayOfYear !== this._dayOfYearLast || s.Time !== this._timeLast) {
      s.DateTime = s.DayOfYear + s.Time / 24;
    } else {
      s.DayOfYear = Math.floor(s.DateTime);
      s.Time = (s.DateTime - s.DayOfYear) * 24;
    }
    this._dateTimeLast = s.DateTime;
    this._dayOfYearLast = s.DayOfYear;
    this._timeLast = s.Time;

    const utcDate = dateTimeToDate(s.DateTime);
    c.utcDate = utcDate;   // exposed for the render layer (e.g. geomagnetic overlay date)
    // Legacy 'heliocentric' BodySource (removed) maps to 'geocentric'
    // — the underlying pipeline always returned geocentric apparent
    // anyway, so no behaviour shift for users with old URL state.
    const bodySource = (s.BodySource === 'heliocentric')
      ? 'geocentric'
      : (s.BodySource || 'geocentric');
    // Ephemeris cache: sun/moon/planet (ra, dec) depend only on date
    // and the selected source pipeline; observer pan / camera drag
    // don't move the cache key. Reusing the previous frame's
    // readings drops 2 Meeus + N planet evaluations per drag tick.
    const _ephemKey = `${utcDate.getTime()}|${bodySource}`;
    if (this._ephemCache && this._ephemCache.key === _ephemKey) {
      this._ephemCacheHit = true;
    } else {
      this._ephemCache = { key: _ephemKey, sun: null, moon: null, planets: Object.create(null) };
      this._ephemCacheHit = false;
    }
    const sunEq  = this._ephemCache.sun  || (this._ephemCache.sun  = bodyRADec('sun',  utcDate, bodySource));
    const moonEq = this._ephemCache.moon || (this._ephemCache.moon = bodyRADec('moon', utcDate, bodySource));
    // sin(horizontal parallax) = R⊕/d (a ratio, no distance). Used below to push
    // each body's local-sky direction to the observer's topocentric place. The
    // Moon's ~1° shift is what makes eclipse magnitude vary by location.
    const sunSinHP  = bodyParallaxSine('sun',  utcDate, bodySource);
    const moonSinHP = bodyParallaxSine('moon', utcDate, bodySource);
    const gmstDeg = greenwichSiderealDeg(utcDate);

    c.SkyRotAngle      = gmstDeg;
    c.TransMatSkyRot   = M.RotatingZ(ToRad(-c.SkyRotAngle));
    c.TransMatCelestToSky = compTransMatCelestToSky(
      s.ObserverLat, s.ObserverLong, c.SkyRotAngle,
    );
    c.TransMatVaultToFe = compTransMatVaultToFe(c.SkyRotAngle);
    c.ObserverFeCoord   = feLatLongToGlobalFeCoord(s.ObserverLat, s.ObserverLong, FE_RADIUS);
    c.TransMatLocalFeToGlobalFe = compTransMatLocalFeToGlobalFe(
      c.ObserverFeCoord, s.ObserverLong,
      s.WorldModel === 'dp' ? s.ObserverLat : null,
    );
    // Heavenly-vault body placement. Apply sky rotation to longitude
    // *before* projecting instead of post-rotating the projected (x, y)
    // about z. AE polar has rotational symmetry about its pole so the
    // two are equivalent there; DP doesn't, so post-rotating leaves the
    // sun / moon / planets / stars off-axis from their disc GPs.
    // Returns a global-FE coord at the body's vault height.
    const _bodyVault = (lat, lonCelest, height) =>
      feInterp.heavenlyVaultCoord(lat, lonCelest, height, c.SkyRotAngle, FE_RADIUS);
    // ObserverAtCenter teleports the camera to the world origin via
    // scene.js but keeps ObserverFeCoord / GlobeObserverCoord at the
    // surface lat / lon — the optical vault therefore stays anchored
    // to the surface position, and dragging the orange dot drags the
    // vault with it so the centre observer can watch projections shift.

    // Globe-Earth observer placement: a unit sphere of radius
    // GLOBE_RADIUS (matching FE_RADIUS so the camera scale is stable).
    // Position uses standard spherical → cartesian; orientation is
    // built so local +x = north, +y = east, +z = up (radial outward),
    // matching the optical-vault hemisphere geometry. Stored as a
    // row-major 3x3 the renderer can copy directly into a Matrix4.
    {
      const GLOBE_RADIUS = FE_RADIUS;
      // GE interpreter: observer basis (north/east/up) and surface position.
      // The local frame is always built from the surface lat/lon, even when
      // the observer is fictitiously placed at the globe centre, so the
      // optical vault keeps its surface tilt. The centre case (GE +
      // ObserverAtCenter) collapses the position to the world origin while
      // the basis still rotates with the orange-dot lat/lon.
      c.GlobeObserverFrame = geInterp.observerBasis(s.ObserverLat, s.ObserverLong);
      c.GlobeObserverCoord = geInterp.observerCoord(
        s.ObserverLat, s.ObserverLong, GLOBE_RADIUS,
        s.ObserverAtCenter && s.WorldModel === 'ge',
      );
      // Celestial sphere expanded to 2·GLOBE_RADIUS so its surface grazes the
      // apex of the observer's optical dome (tangent at the observer with
      // radius FE_RADIUS, so its apex is 2·FE_RADIUS from the world origin).
      c.GlobeVaultRadius = GLOBE_RADIUS * 2;
    }
    // helper: place a celestial point on the globe heavenly-vault
    // shell at (declination, GP longitude). GP longitude folds GMST
    // out of RA so the vault co-rotates with Earth — same convention
    // the FE vault uses, just on a sphere instead of a dome.
    const _globeVaultAt = (decDeg, gpLonDeg) =>
      geInterp.heavenlyVaultCoord(decDeg, gpLonDeg, c.GlobeVaultRadius);
    const _wrapLon180 = (x) => ((x + 180) % 360 + 360) % 360 - 180;

    // GE optical-vault projection. Takes a `localSky` vector
    // (components: zenith, east, north) and returns the body's
    // world position on the GE optical cap (hemisphere of radius
    // FE_RADIUS tangent at the observer). Sub-horizon bodies
    // (zenith ≤ 0) are parked at the far-below sentinel so they
    // disappear as their elevation crosses the horizon — same
    // convention as the FE vault.
    const _globeOpticalProject = (localSky) =>
      geInterp.opticalProject(localSky, c.GlobeObserverFrame, c.GlobeObserverCoord, FE_RADIUS);

    // Refraction: lift every body's optical-vault local-sky vector
    // by the apparent-altitude refraction. True positions
    // (`*VaultCoord`, `*GlobeVaultCoord`, `*OpticalVaultCoordTrue`,
    // `*GlobeOpticalVaultCoordTrue`) and HUD readouts
    // (`*AnglesGlobe`) remain unrefracted, so the user can compare
    // apparent vs true. `_opticalCoords(lg)` returns the pair:
    //   { app: refracted local-sky (used for the rendered marker),
    //     true: original local-sky (used by the geocentric ghost) }
    // Plus the refraction value in degrees so the HUD can display it.
    const _refrMode = s.Refraction || 'off';
    const _refrPressure = Number.isFinite(Number(s.RefractionPressureMbar))
      ? Number(s.RefractionPressureMbar) : 1013.25;
    const _refrTempC = Number.isFinite(Number(s.RefractionTemperatureC))
      ? Number(s.RefractionTemperatureC) : 15;
    const _refr = (lg) => applyRefractionLocalSky(lg, _refrMode, _refrPressure, _refrTempC);
    const _opticalPair = (lg) => {
      const app = _refrMode === 'off' ? lg : _refr(lg);
      return { lgTrue: lg, lgApp: app };
    };

    // --- sun ---
    c.SunRA = sunEq.ra; c.SunDec = sunEq.dec;
    c.SunCelestCoord   = equatorialToCelestCoord(sunEq);
    c.SunCelestLatLong = coordToLatLong(c.SunCelestCoord);
    c.SunCelestAngle   = c.SunCelestLatLong.lng;
    // Sun is master elevation reference. Moon scales by its dec-range
    // ratio; planets share SUN_RANGE.
    const HEADROOM = 0.06;
    const SUN_RANGE    = 0.20;
    const SUN_DEC_DEG  = 23.44;
    const MOON_DEC_DEG = 28.50;
    const sunDecNorm = 0.5 + 0.5 * Math.max(-1, Math.min(1,
      c.SunCelestLatLong.lat / SUN_DEC_DEG));
    const sunCeil = heavenlyVaultCeiling(
      c.SunCelestLatLong.lat, s.VaultSize, s.VaultHeight, FE_RADIUS,
    );
    s.SunVaultHeight = Math.min(
      sunCeil,
      s.StarfieldVaultHeight + HEADROOM + sunDecNorm * SUN_RANGE,
    );
    c.SunGlobeVaultCoord = _globeVaultAt(
      c.SunCelestLatLong.lat,
      _wrapLon180(c.SunRA * 180 / Math.PI - c.SkyRotAngle),
    );
    c.SunVaultCoord = _bodyVault(
      c.SunCelestLatLong.lat, c.SunCelestLatLong.lng, s.SunVaultHeight,
    );
    // Topocentric parallax: push the geocentric direction toward the horizon by
    // the body's horizontal parallax (the R⊕/d ratio, no distance). Only the
    // observer's local sky shifts; the shared geocentric vault is untouched.
    c.SunLocalSkyCoord = parallaxAltitudeLocalSky(
      celestCoordToLocalSkyCoord(c.SunCelestCoord, c.TransMatCelestToSky),
      sunSinHP,
    );
    c.SunAnglesGlobe     = localSkyCoordToAngles(c.SunLocalSkyCoord);
    {
      const { lgTrue, lgApp } = _opticalPair(c.SunLocalSkyCoord);
      c.SunOpticalVaultCoordTrue = localSkyCoordToGlobalFeCoord(
        opticalVaultProject(lgTrue, c.OpticalVaultRadius, c.OpticalVaultHeightEffective),
        c.TransMatLocalFeToGlobalFe,
      );
      c.SunGlobeOpticalVaultCoordTrue = _globeOpticalProject(lgTrue);
      c.SunOpticalVaultCoord = lgApp === lgTrue
        ? c.SunOpticalVaultCoordTrue
        : localSkyCoordToGlobalFeCoord(
            opticalVaultProject(lgApp, c.OpticalVaultRadius, c.OpticalVaultHeightEffective),
            c.TransMatLocalFeToGlobalFe,
          );
      c.SunGlobeOpticalVaultCoord = lgApp === lgTrue
        ? c.SunGlobeOpticalVaultCoordTrue
        : _globeOpticalProject(lgApp);
    }

    // --- moon ---
    c.MoonRA = moonEq.ra; c.MoonDec = moonEq.dec;
    c.MoonCelestCoord    = equatorialToCelestCoord(moonEq);
    c.MoonCelestLatLong  = coordToLatLong(c.MoonCelestCoord);
    c.MoonCelestAngle    = c.MoonCelestLatLong.lng;
    c.MoonNorthCelestCoord = [0, 0, 1];
    // Moon slaves to the sun's ecliptic too — height = sun's
    // current vault z plus a small offset proportional to the
    // moon's ecliptic latitude β (lunar inclination ±5.14°). β
    // computed by rotating the equatorial unit vector
    // (RA, Dec) by the obliquity ε about the x-axis, taking
    // arcsin of the z component. With β = 0 the moon sits at the
    // sun's exact height — the geometric pre-condition for solar
    // eclipses, so the eclipse demos visually align when the
    // moon crosses the ecliptic.
    const ECL_OBLIQ_RAD = SUN_DEC_DEG * Math.PI / 180;
    const ECL_COS = Math.cos(ECL_OBLIQ_RAD);
    const ECL_SIN = Math.sin(ECL_OBLIQ_RAD);
    const eclipticBeta = (raRad, decRad) => Math.asin(
      Math.max(-1, Math.min(1,
        ECL_COS * Math.sin(decRad)
        - ECL_SIN * Math.cos(decRad) * Math.sin(raRad),
      )),
    );
    // Slope: 0.20 / 23.44° ≈ 0.00427 per degree. Same slope the
    // sun uses for dec → height, so β offsets sit on the same
    // proportional scale as the rest of the dome.
    const ECLIPTIC_HEIGHT_PER_DEG = SUN_RANGE / (2 * SUN_DEC_DEG);
    const moonBetaDeg = eclipticBeta(c.MoonRA, c.MoonDec) * 180 / Math.PI;
    const moonCeil = heavenlyVaultCeiling(
      c.MoonCelestLatLong.lat, s.VaultSize, s.VaultHeight, FE_RADIUS,
    );
    const moonFloor = s.StarfieldVaultHeight + HEADROOM;
    s.MoonVaultHeight = Math.max(
      moonFloor,
      Math.min(moonCeil, s.SunVaultHeight + moonBetaDeg * ECLIPTIC_HEIGHT_PER_DEG),
    );
    c.MoonGlobeVaultCoord = _globeVaultAt(
      c.MoonCelestLatLong.lat,
      _wrapLon180(c.MoonRA * 180 / Math.PI - c.SkyRotAngle),
    );
    c.MoonVaultCoord = _bodyVault(
      c.MoonCelestLatLong.lat, c.MoonCelestLatLong.lng, s.MoonVaultHeight,
    );
    // Topocentric parallax (the dominant one, up to ~1° at the horizon).
    c.MoonLocalSkyCoord = parallaxAltitudeLocalSky(
      celestCoordToLocalSkyCoord(c.MoonCelestCoord, c.TransMatCelestToSky),
      moonSinHP,
    );
    c.MoonAnglesGlobe     = localSkyCoordToAngles(c.MoonLocalSkyCoord);
    {
      const { lgTrue, lgApp } = _opticalPair(c.MoonLocalSkyCoord);
      c.MoonOpticalVaultCoordTrue = localSkyCoordToGlobalFeCoord(
        opticalVaultProject(lgTrue, c.OpticalVaultRadius, c.OpticalVaultHeightEffective),
        c.TransMatLocalFeToGlobalFe,
      );
      c.MoonGlobeOpticalVaultCoordTrue = _globeOpticalProject(lgTrue);
      c.MoonOpticalVaultCoord = lgApp === lgTrue
        ? c.MoonOpticalVaultCoordTrue
        : localSkyCoordToGlobalFeCoord(
            opticalVaultProject(lgApp, c.OpticalVaultRadius, c.OpticalVaultHeightEffective),
            c.TransMatLocalFeToGlobalFe,
          );
      c.MoonGlobeOpticalVaultCoord = lgApp === lgTrue
        ? c.MoonGlobeOpticalVaultCoordTrue
        : _globeOpticalProject(lgApp);
    }

    // --- analemma accumulators ---
    // One vault-coord point per integer day-of-year while the
    // corresponding flag is on. Cleared whenever the cache key
    // (observer / time-of-day / year / bodySource) changes.
    // WorldModel folded into the cache key: switching FE↔GE resets
    // the trace because the source coord changes (FE optical-vault →
    // globe optical-vault projection).
    const _ge = s.WorldModel === 'ge';
    const analKey = `${s.WorldModel}|${s.ObserverLat}|${s.ObserverLong}|${s.ObserverHeading}|${Math.round(s.Time*1000)/1000}|${utcDate.getUTCFullYear()}|${bodySource}`;
    this._sunAnalemma  = this._sunAnalemma  || { points: [], lastDay: -1, key: null };
    this._moonAnalemma = this._moonAnalemma || { points: [], lastDay: -1, key: null };
    const stepAnalemma = (slot, flag, srcCoord) => {
      if (!flag) {
        slot.points.length = 0; slot.lastDay = -1; slot.key = null;
        return;
      }
      if (slot.key !== analKey) {
        slot.points.length = 0; slot.lastDay = -1; slot.key = analKey;
      }
      // Skip below-horizon sentinel — same convention as stepVaultArc.
      if (!srcCoord || srcCoord[2] === -1000) return;
      if (s.DayOfYear !== slot.lastDay) {
        slot.points.push(srcCoord[0], srcCoord[1], srcCoord[2]);
        slot.lastDay = s.DayOfYear;
      }
    };
    const sunOptSrc  = _ge ? (c.SunGlobeOpticalVaultCoord  || c.SunOpticalVaultCoord)  : c.SunOpticalVaultCoord;
    const moonOptSrc = _ge ? (c.MoonGlobeOpticalVaultCoord || c.MoonOpticalVaultCoord) : c.MoonOpticalVaultCoord;
    stepAnalemma(this._sunAnalemma,  s.ShowSunAnalemma,  sunOptSrc);
    stepAnalemma(this._moonAnalemma, s.ShowMoonAnalemma, moonOptSrc);
    c.SunAnalemmaPoints  = this._sunAnalemma.points;
    c.MoonAnalemmaPoints = this._moonAnalemma.points;

    // Heavenly-vault sun-arc accumulator: appends the heavenly vault
    // sun coord every frame the flag is on. Disc-anchored (not
    // observer-local), so the resulting curve sits over the disc grid
    // — that's what binds the analemma to the FE sky like the
    // tropic / equator rings imply. Reset on flag off→on.
    const stepVaultArc = (slot, on, srcCoord) => {
      if (!on) {
        slot.points.length = 0;
        slot.wasOn = false;
        slot.key = null;
        return;
      }
      const arcKey = `${s.WorldModel}|${s.ObserverLat}|${utcDate.getUTCFullYear()}|${bodySource}`;
      if (!slot.wasOn || slot.key !== arcKey) {
        slot.points.length = 0;
        slot.key = arcKey;
        slot.wasOn = true;
      }
      // Skip the below-horizon sentinel ([0, 0, -1000]) the GE
      // optical-vault projection emits — appending it produces a
      // vertical line dropping off the sphere.
      if (!srcCoord || srcCoord[2] === -1000) return;
      const pts = slot.points;
      const n = pts.length;
      if (n < 3
          || pts[n - 3] !== srcCoord[0]
          || pts[n - 2] !== srcCoord[1]
          || pts[n - 1] !== srcCoord[2]) {
        pts.push(srcCoord[0], srcCoord[1], srcCoord[2]);
      }
    };
    this._sunVaultArc  = this._sunVaultArc  || { points: [], wasOn: false, key: null };
    this._moonVaultArc = this._moonVaultArc || { points: [], wasOn: false, key: null };
    // Both modes use the observer-local optical-vault coord. Skip
    // capture when the body is below the local horizon — that's
    // what a physical observer would (not) see, so the rendered
    // trace matches their actual sky. Polar observers naturally
    // get a partial figure (5-7 months only).
    const _sunAboveHoriz  = c.SunAnglesGlobe  && c.SunAnglesGlobe.elevation  > 0;
    const _moonAboveHoriz = c.MoonAnglesGlobe && c.MoonAnglesGlobe.elevation > 0;
    const sunArcSrc  = _sunAboveHoriz
      ? (_ge ? (c.SunGlobeOpticalVaultCoord || c.SunOpticalVaultCoord) : c.SunOpticalVaultCoord)
      : null;
    const moonArcSrc = _moonAboveHoriz
      ? (_ge ? (c.MoonGlobeOpticalVaultCoord || c.MoonOpticalVaultCoord) : c.MoonOpticalVaultCoord)
      : null;
    stepVaultArc(this._sunVaultArc,  s.SunVaultArcOn,  sunArcSrc);
    stepVaultArc(this._moonVaultArc, s.MoonVaultArcOn, moonArcSrc);
    c.SunVaultArcPoints  = this._sunVaultArc.points;
    c.MoonVaultArcPoints = this._moonVaultArc.points;

    // Dome caustic: optional. When ShowDomeCaustic is on, ray-trace
    // the heavenly-vault interior reflection of the sun and bin the
    // hits into a density grid; renderer reads the local-max peaks.
    if (s.ShowDomeCaustic) {
      const domeR = s.VaultSize * FE_RADIUS;
      const domeH = s.VaultHeight;

      // Cache key — re-trace only when sun position, dome shape, or
      // observer placement actually changed. Each setState triggers a
      // full update(), so without the cache the 28k-ray trace would
      // run every time a UI toggle flips, dragging the Heavenly ↔
      // Optical swap into multi-frame stalls.
      const sv = c.SunVaultCoord;
      const ob = c.ObserverFeCoord || [0, 0, 0];
      const key = `${sv[0].toFixed(5)}|${sv[1].toFixed(5)}|${sv[2].toFixed(5)}|`
                + `${domeR.toFixed(4)}|${domeH.toFixed(4)}|`
                + `${ob[0].toFixed(5)}|${ob[1].toFixed(5)}|${ob[2].toFixed(5)}`;
      let result;
      if (this._domeCausticKey === key && this._domeCausticResult) {
        result = this._domeCausticResult;
      } else {
        result = traceDomeCaustic({
          sunPos:  sv,
          domeR, domeH,
          discClipR: FE_RADIUS * 1.4,
          nTheta: 240, nPhi: 120,
          observerCoord: ob,
        });
        this._domeCausticKey = key;
        this._domeCausticResult = result;
      }
      c.DomeCausticPeaks    = result.peaks;
      c.DomeCausticPeakMax  = result.peakMax;
      c.DomeCausticPeakSun  = result.peakSun;

      // Optical-vault orange ghost sun — mirror of the apparent sun
      // through the observer's zenith axis. Light from the real sun
      // reaches the observer along two paths: the direct line (the
      // apparent sun) and a wrap-around path through the dome that,
      // by symmetry, converges at the same elevation but the
      // antipodal azimuth. As the apparent sun (yellow) traces its
      // arc, the orange traces an exactly mirrored arc on the
      // opposite side at the same elevation.
      const opticals = [];
      const obs = c.ObserverFeCoord || [0, 0, 0];
      const sopt = c.SunOpticalVaultCoord;
      if (sopt) {
        const lx = sopt[0] - obs[0];
        const ly = sopt[1] - obs[1];
        const lz = sopt[2] - obs[2];
        if (lz > 0) {
          opticals.push({
            x: obs[0] - lx,
            y: obs[1] - ly,
            z: obs[2] + lz,
            intensity: 1,
          });
        }
      }
      c.DomeCausticOpticalPeaks = opticals;
    } else {
      c.DomeCausticPeaks         = null;
      c.DomeCausticPeakMax       = 0;
      c.DomeCausticPeakSun       = null;
      c.DomeCausticOpticalPeaks  = null;
      this._domeCausticKey = null;
      this._domeCausticResult = null;
    }

    // Moon phase. Both `SunCelestCoord` and `MoonCelestCoord` are
    // unit direction vectors from `equatorialToCelestCoord` of the
    // body's geocentric apparent RA / Dec — distance / AU are not
    // involved. The "sun-at-infinity" approximation
    // (moon→sun ≈ SunCelestCoord) introduces ~0.5° error worst-case
    // (sin(parallax) ≈ moon-AU / sun-AU ≈ 0.0026).
    // `MoonPhase` is in [0, π]: 0 = full (sun and moon on opposite
    // celestial directions, terminator at 1.0 illumination),
    // π = new. `MoonPhaseFraction` = 0.5·(1 + cos MoonPhase) maps
    // back to a [0..1] illuminated fraction.
    const moonToGlobe = V.Norm(V.Scale(c.MoonCelestCoord, -1));
    const moonToSun   = V.Norm(V.Sub(c.SunCelestCoord, V.Scale(c.MoonCelestCoord, 0)));
    const shadowUp    = V.Norm(V.Mult(moonToSun, moonToGlobe));
    c.MoonPhase = Math.acos(Limit1(V.ScalarProd(moonToSun, moonToGlobe)));
    c.MoonPhaseFraction = 0.5 * (1 + Math.cos(c.MoonPhase));

    // Terminator rotation as seen from the observer.
    const globeToMoon = V.Scale(celestCoordToLocalSkyCoord(
      V.Scale(moonToGlobe, -1), c.TransMatCelestToSky,
    ), 1);
    let camRight = V.Mult(globeToMoon, [1, 0, 0]);
    if (V.Length(camRight) === 0) {
      camRight = V.Mult(globeToMoon, [0, 1, 0]);
    }
    camRight = V.Norm(camRight);
    const camUp = V.Mult(camRight, globeToMoon);
    const moonShadowUpLocal = celestCoordToLocalSkyCoord(shadowUp, c.TransMatCelestToSky);
    let rot = Math.acos(Limit1(V.ScalarProd(camUp, moonShadowUpLocal)));
    if (V.ScalarProd(moonShadowUpLocal, camRight) > 0) rot = -rot;
    c.MoonRotation = rot;

    // Optical vault dimensions. Cap height clamped under VaultHeight.
    // Inside Optical mode H := R (strict hemisphere, 1:1 elevation).
    // In Heavenly H := OpticalVaultHeight (flattened cap depiction).
    // In GE the cap geometry "wraps" the upper hemisphere of the
    // terrestrial sphere — apex at the observer, rim on the great
    // circle 90° from the observer's zenith. Radius and height both
    // = FE_RADIUS so the unit hemisphere mesh, after the renderer
    // anchors it at the world origin, exactly covers the visible
    // half of the terrestrial sphere.
    if (s.WorldModel === 'ge') {
      c.OpticalVaultRadius = FE_RADIUS;
      c.OpticalVaultHeight = FE_RADIUS;
    } else {
      c.OpticalVaultRadius = s.OpticalVaultSize;
      c.OpticalVaultHeight = Math.min(s.OpticalVaultHeight, s.VaultHeight);
    }
    c.OpticalVaultHeightEffective = s.InsideVault
      ? c.OpticalVaultRadius
      : c.OpticalVaultHeight;

    // Linear fade across civil twilight: 0 at sun elev ≥ 0°, 1 at ≤ -12°.
    if (s.PermanentNight) {
      c.NightFactor = 1;
    } else {
      const sunElev = c.SunAnglesGlobe.elevation;
      c.NightFactor = Limit01((-sunElev) / 12.0);
    }

    // Live solar-eclipse path detector. When the simulator clock
    // sits inside the per-event window of a catalogued eclipse,
    // build the polynomial-driven shadow path from NASA's
    // published Besselian elements. Falls back to a per-pipeline
    // sublunar approximation for eclipses not in the Bessel table.
    // The catalogue anchor's time-of-day is coarse (cycle-derived, up to ~½ day
    // from the true greatest), so the gate is wide enough that the clock can be
    // at the real greatest while still matching the nearest catalogue anchor;
    // the precise greatest + tight display window come from the path below.
    const HUNT_HALF_WINDOW_DAYS = 18 / 24;
    const _liveEclipse = s.LiveEclipseShadows !== false
      ? findNearestSolarEclipse(s.DateTime) : null;
    if (_liveEclipse && _liveEclipse.distDays < HUNT_HALF_WINDOW_DAYS) {
      const cacheKey = `${_liveEclipse.anchorMs}|${bodySource}`;
      if (!this._liveEclipsePathCache
          || this._liveEclipsePathCache.key !== cacheKey) {
        let els = ECLIPSE_BESSELIAN[_liveEclipse.date];
        if (!els && /^\d{4}-\d{2}-\d{2}$/.test(_liveEclipse.date)) {
          // The catalogue's greatest-eclipse date can sit ±1 day from the
          // Besselian table's published date; try the neighbours so the
          // precise shadow still resolves before the sublunar fallback.
          const base = Date.parse(_liveEclipse.date + 'T00:00:00Z');
          for (const off of [86400000, -86400000]) {
            const k = new Date(base + off).toISOString().slice(0, 10);
            if (ECLIPSE_BESSELIAN[k]) { els = ECLIPSE_BESSELIAN[k]; break; }
          }
        }
        if (els) {
          const result = besselianShadowPathFromElements(els, 49);
          const greatestDt = result.greatest / TIME_ORIGIN.msPerDay - TIME_ORIGIN.ZeroDate;
          const halfWindowMs = Math.max(
            result.greatest - result.p1,
            result.p4 - result.greatest,
          );
          // Convert sample t (hours from t0Tdt) so renderer
          // sweeps relative to greatest. Path samples carry
          // (lat, lon); progress comes from anchor + halfWindow.
          const path = result.samples
            .filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lon))
            .map((p) => ({ t: p.t, lat: p.lat, lon: p.lon }));
          this._liveEclipsePathCache = {
            key:        cacheKey,
            path,
            anchorDt:   greatestDt,
            halfWindow: halfWindowMs / TIME_ORIGIN.msPerDay,
            l1:         result.l1Greatest,
            l2:         result.l2Greatest,
          };
        } else {
          // Fallback: ephemeris-derived sublunar trace +
          // separation-threshold contact window.
          const sunFn  = (d) => bodyRADec('sun',  d, bodySource);
          const moonFn = (d) => bodyRADec('moon', d, bodySource);
          // The catalogue anchor is coarse; refine to the true greatest in a
          // wide window first so the contact-window walk starts at conjunction.
          const approx = refineEclipseByMinSeparation(
            new Date(_liveEclipse.anchorMs), sunFn, moonFn,
            { kind: 'solar', halfWindowMinutes: 18 * 60 }).date;
          const contact = findSolarEclipseContactWindow(approx, sunFn, moonFn);
          const halfWindowDays = contact.halfWindowMs / TIME_ORIGIN.msPerDay;
          const greatestDt = contact.greatestMs / TIME_ORIGIN.msPerDay - TIME_ORIGIN.ZeroDate;
          const path = computeSolarEclipseShadowPath(
            new Date(contact.greatestMs), moonFn,
            halfWindowDays * 24, 49);
          this._liveEclipsePathCache = {
            key:        cacheKey,
            path,
            anchorDt:   greatestDt,
            halfWindow: halfWindowDays,
            l1:         null,
            l2:         null,
          };
        }
      }
      const cached = this._liveEclipsePathCache;
      const dtFromGreatest = s.DateTime - cached.anchorDt;
      if (Math.abs(dtFromGreatest) <= cached.halfWindow) {
        c.LiveEclipseShadowPath           = cached.path;
        c.LiveEclipseShadowAnchorDt       = cached.anchorDt;
        c.LiveEclipseShadowHalfWindowDays = cached.halfWindow;
        c.LiveEclipseShadowL1             = cached.l1;
        c.LiveEclipseShadowL2             = cached.l2;
      } else {
        c.LiveEclipseShadowPath           = null;
        c.LiveEclipseShadowAnchorDt       = null;
        c.LiveEclipseShadowHalfWindowDays = null;
        c.LiveEclipseShadowL1             = null;
        c.LiveEclipseShadowL2             = null;
      }
    } else {
      c.LiveEclipseShadowPath           = null;
      c.LiveEclipseShadowAnchorDt       = null;
      c.LiveEclipseShadowHalfWindowDays = null;
      c.LiveEclipseShadowL1             = null;
      c.LiveEclipseShadowL2             = null;
      this._liveEclipsePathCache        = null;
    }

    // Planets slave to the sun's ecliptic. Default height tracks
    // s.SunVaultHeight plus a small offset proportional to the
    // planet's ecliptic latitude β. Real-world solar-system
    // |β|max stays under ~7° (Mercury) so the offset is tiny in
    // proportion to the sim's height range — the offsets read as
    // a thin band of bodies riding the sun's arc.
    const PLANET_RANGE_KEY = {
      mercury: 'MercuryVaultHeight', venus: 'VenusVaultHeight',
      mars: 'MarsVaultHeight', jupiter: 'JupiterVaultHeight',
      saturn: 'SaturnVaultHeight',
      uranus: 'UranusVaultHeight', neptune: 'NeptuneVaultHeight',
    };
    const sunVaultZ = s.SunVaultHeight;

    c.Planets = {};
    for (const name of PLANET_NAMES) {
      const eq = this._ephemCache.planets[name]
        || (this._ephemCache.planets[name] = bodyRADec(name, utcDate, bodySource));
      // NaN = pipeline lacks this body; skip geometry + marker.
      if (!Number.isFinite(eq.ra) || !Number.isFinite(eq.dec)) continue;
      const celestCoord = equatorialToCelestCoord(eq);
      const ll = coordToLatLong(celestCoord);
      // Default height: sun's vault z + (planet's ecliptic
      // latitude β) × the same dec → height slope the sun uses
      // (`SUN_RANGE / (2·SUN_DEC_DEG)` ≈ 0.00427 / °). With β =
      // 0 a planet sits exactly at the sun's height — the
      // visualisation places every body on the sun's arc with a
      // small perpendicular offset for its inclination off the
      // ecliptic plane. Manual panel slider edits aren't
      // preserved across frames yet; a future serial can add
      // `*VaultManual` flags to lock manual values.
      const planetBetaDeg = eclipticBeta(eq.ra, eq.dec) * 180 / Math.PI;
      const desired = sunVaultZ + planetBetaDeg * ECLIPTIC_HEIGHT_PER_DEG;
      const planetCeil = heavenlyVaultCeiling(ll.lat, s.VaultSize, s.VaultHeight, FE_RADIUS);
      const planetFloor = s.StarfieldVaultHeight + HEADROOM;
      const planetZ = Math.max(planetFloor, Math.min(planetCeil, desired));
      s[PLANET_RANGE_KEY[name]] = planetZ;
      const vaultCoord = _bodyVault(ll.lat, ll.lng, planetZ);
      const globeVaultCoord = _globeVaultAt(
        ll.lat,
        _wrapLon180(eq.ra * 180 / Math.PI - c.SkyRotAngle),
      );
      const localSky = parallaxAltitudeLocalSky(
        celestCoordToLocalSkyCoord(celestCoord, c.TransMatCelestToSky),
        bodyParallaxSine(name, utcDate, bodySource),
      );
      const anglesGlobe = localSkyCoordToAngles(localSky);
      const { lgTrue, lgApp } = _opticalPair(localSky);
      const opticalVaultCoordTrue = localSkyCoordToGlobalFeCoord(
        opticalVaultProject(lgTrue, c.OpticalVaultRadius, c.OpticalVaultHeightEffective),
        c.TransMatLocalFeToGlobalFe,
      );
      const globeOpticalVaultCoordTrue = _globeOpticalProject(lgTrue);
      const opticalVaultCoord = lgApp === lgTrue
        ? opticalVaultCoordTrue
        : localSkyCoordToGlobalFeCoord(
            opticalVaultProject(lgApp, c.OpticalVaultRadius, c.OpticalVaultHeightEffective),
            c.TransMatLocalFeToGlobalFe,
          );
      const globeOpticalVaultCoord = lgApp === lgTrue
        ? globeOpticalVaultCoordTrue
        : _globeOpticalProject(lgApp);
      c.Planets[name] = {
        ra: eq.ra, dec: eq.dec,
        celestCoord, celestLatLong: ll,
        vaultCoord, globeVaultCoord, opticalVaultCoord, globeOpticalVaultCoord,
        opticalVaultCoordTrue, globeOpticalVaultCoordTrue,
        anglesGlobe,
      };
    }

    // Star projection. Trepidation master forces all three apparent-of-
    // date corrections; otherwise the three booleans apply independently.
    const starOpts = s.StarTrepidation
      ? { precession: true, nutation: true, aberration: true }
      : {
          precession: !!s.StarApplyPrecession,
          nutation:   !!s.StarApplyNutation,
          aberration: !!s.StarApplyAberration,
        };
    const STAR_VAULT_HEIGHT = s.StarfieldVaultHeight;
    // Apparent (RA, Dec) per star is pure (J2000, date, starOpts);
    // observer-pan / camera drags don't change any of those, so
    // cache by (dateMs, starOptsKey) and reuse across drag ticks.
    // The downstream projection (vault / local-sky / optical) is
    // observer-dependent and still recomputes every frame.
    const _starOptsKey = `${starOpts.precession ? 1 : 0}${starOpts.nutation ? 1 : 0}${starOpts.aberration ? 1 : 0}`;
    const _starApparentKey = `${utcDate.getTime()}|${_starOptsKey}`;
    if (!this._starApparentCache || this._starApparentCache.key !== _starApparentKey) {
      this._starApparentCache = { key: _starApparentKey, byId: new Map() };
    }
    const _starApparentById = this._starApparentCache.byId;
    const projectStar = (star) => {
      let apparent = _starApparentById.get(star.id);
      if (!apparent) {
        const raJ2000  = (star.raH / 24) * 2 * Math.PI;
        const decJ2000 = star.decD * Math.PI / 180;
        apparent = apparentStarPosition(raJ2000, decJ2000, utcDate, starOpts);
        _starApparentById.set(star.id, apparent);
      }
      const ra  = apparent.ra;
      const dec = apparent.dec;
      const celestCoord   = equatorialToCelestCoord({ ra, dec });
      const celestLatLong = coordToLatLong(celestCoord);
      const vaultCoord    = _bodyVault(celestLatLong.lat, celestLatLong.lng, STAR_VAULT_HEIGHT);
      const globeVaultCoord = _globeVaultAt(
        celestLatLong.lat,
        _wrapLon180(ra * 180 / Math.PI - c.SkyRotAngle),
      );
      const localSky  = celestCoordToLocalSkyCoord(celestCoord, c.TransMatCelestToSky);
      const anglesGlobe = localSkyCoordToAngles(localSky);
      const { lgTrue, lgApp } = _opticalPair(localSky);
      const opticalVaultCoordTrue = localSkyCoordToGlobalFeCoord(
        opticalVaultProject(lgTrue, c.OpticalVaultRadius, c.OpticalVaultHeightEffective),
        c.TransMatLocalFeToGlobalFe,
      );
      const globeOpticalVaultCoordTrue = _globeOpticalProject(lgTrue);
      const opticalVaultCoord = lgApp === lgTrue
        ? opticalVaultCoordTrue
        : localSkyCoordToGlobalFeCoord(
            opticalVaultProject(lgApp, c.OpticalVaultRadius, c.OpticalVaultHeightEffective),
            c.TransMatLocalFeToGlobalFe,
          );
      const globeOpticalVaultCoord = lgApp === lgTrue
        ? globeOpticalVaultCoordTrue
        : _globeOpticalProject(lgApp);
      return {
        id:   star.id,
        name: star.name,
        mag:  star.mag,
        ra, dec,
        celestCoord, celestLatLong,
        vaultCoord, globeVaultCoord, opticalVaultCoord, globeOpticalVaultCoord,
        opticalVaultCoordTrue, globeOpticalVaultCoordTrue,
        anglesGlobe,
      };
    };
    // Skip the per-frame catalogue projection when stars are hidden
    // AND nothing in the tracker / follow target references a
    // catalogue star. Satellite ids (`star:sat_*`) are projected by
    // the satellite block below, so they aren't a reason to project
    // here. Mirrors the gate already used for satellites.
    const _trackerHasStar = (Array.isArray(s.TrackerTargets) ? s.TrackerTargets : [])
      .some((t) => typeof t === 'string' && t.startsWith('star:') && !t.startsWith('star:sat_'));
    const _followIsStar = typeof s.FollowTarget === 'string'
      && s.FollowTarget.startsWith('star:') && !s.FollowTarget.startsWith('star:sat_');
    if (s.ShowStars !== false || _trackerHasStar || _followIsStar) {
      c.CelNavStars     = CEL_NAV_STARS.map(projectStar);
      c.CataloguedStars = CATALOGUED_STARS.map(projectStar);
      c.BlackHoles      = BLACK_HOLES.map(projectStar);
      c.Quasars         = QUASARS.map(projectStar);
      c.Galaxies        = GALAXIES.map(projectStar);
      c.CelTheoStars    = CEL_THEO_OWN.map(projectStar);
    } else {
      c.CelNavStars = [];
      c.CataloguedStars = [];
      c.BlackHoles = [];
      c.Quasars = [];
      c.Galaxies = [];
      c.CelTheoStars = [];
    }

    // Satellites: sub-point (lat, lon) computed per-frame from
    // two-body Kepler; projected through the same vault /
    // local-sky / optical-vault machinery as stars. Built when
    // ShowSatellites is on OR when any `star:sat_*` id sits in the
    // tracker — so per-chip clicks alone are enough to make a
    // satellite render without also flipping the master.
    const SAT_VAULT_HEIGHT = 0.15;
    const _trackerHasSat = (Array.isArray(s.TrackerTargets) ? s.TrackerTargets : [])
      .some((t) => typeof t === 'string' && t.startsWith('star:sat_'));
    const _followIsSat = typeof s.FollowTarget === 'string' && s.FollowTarget.startsWith('star:sat_');
    if (s.ShowSatellites || _trackerHasSat || _followIsSat) {
      const projectSatellite = (sat) => {
        const sub = satelliteSubPoint(sat, utcDate);
        const decRad = sub.lat * Math.PI / 180;
        const raRad  = (sub.lon + c.SkyRotAngle) * Math.PI / 180;
        const celestCoord   = equatorialToCelestCoord({ ra: raRad, dec: decRad });
        const celestLatLong = coordToLatLong(celestCoord);
        const vaultCoord    = _bodyVault(celestLatLong.lat, celestLatLong.lng, SAT_VAULT_HEIGHT);
        const globeVaultCoord = _globeVaultAt(celestLatLong.lat, sub.lon);
        const localSky  = celestCoordToLocalSkyCoord(celestCoord, c.TransMatCelestToSky);
        const anglesGlobe = localSkyCoordToAngles(localSky);
        const { lgTrue, lgApp } = _opticalPair(localSky);
        const opticalVaultCoordTrue = localSkyCoordToGlobalFeCoord(
          opticalVaultProject(lgTrue, c.OpticalVaultRadius, c.OpticalVaultHeightEffective),
          c.TransMatLocalFeToGlobalFe,
        );
        const globeOpticalVaultCoordTrue = _globeOpticalProject(lgTrue);
        const opticalVaultCoord = lgApp === lgTrue
          ? opticalVaultCoordTrue
          : localSkyCoordToGlobalFeCoord(
              opticalVaultProject(lgApp, c.OpticalVaultRadius, c.OpticalVaultHeightEffective),
              c.TransMatLocalFeToGlobalFe,
            );
        const globeOpticalVaultCoord = lgApp === lgTrue
          ? globeOpticalVaultCoordTrue
          : _globeOpticalProject(lgApp);
        return {
          id: sat.id, name: sat.name,
          ra: raRad, dec: decRad,
          celestCoord, celestLatLong, globeVaultCoord,
          vaultCoord, opticalVaultCoord, globeOpticalVaultCoord,
          opticalVaultCoordTrue, globeOpticalVaultCoordTrue,
          anglesGlobe,
        };
      };
      c.Satellites = [...SATELLITES, ...SATELLITES_EXTRA].map(projectSatellite);
    } else {
      c.Satellites = [];
    }

    // GP path overlay: per-category 24 h sub-point traces. Flat map
    // from a unique id → { pts, color } so the renderer doesn't need
    // category metadata. Each category's contribution is gated by its
    // own GPPath<Category> state flag.
    c.GPPaths = {};
    const gmstDegAt = (date) => {
      const jd = date.getTime() / 86400000 + 2440587.5;
      const T = (jd - 2451545.0) / 36525;
      let g = (280.46061837 + 360.98564736629 * (jd - 2451545.0)
              + 0.000387933 * T * T) % 360;
      if (g < 0) g += 360;
      return g;
    };
    const dayMs  = utcDate.getTime();
    // GP path span. Default 1 day = the body's diurnal rotation.
    // When the user bumps `GPPathDays` up to e.g. 365, the trace
    // covers a full year so planets / sun / moon's declination
    // drift is visible as a band, not just a circle. Sample count
    // scales with span so the trace stays smooth for both short
    // and long horizons.
    const _gpDays   = Math.max(0.0417, Math.min(3650, +s.GPPathDays || 1));
    const _gpSpanMs = _gpDays * 86400000;
    const N_GP = Math.max(48, Math.min(2048, Math.round(48 * Math.sqrt(_gpDays))));
    const sampleFrom = (getRaDec) => {
      const pts = [];
      const latLon = [];
      for (let i = 0; i <= N_GP; i++) {
        const d = new Date(dayMs + (i / N_GP) * _gpSpanMs);
        const eq = getRaDec(d);
        if (!eq || !Number.isFinite(eq.ra) || !Number.isFinite(eq.dec)) return null;
        const gpLat = eq.dec * 180 / Math.PI;
        const raDeg = ((eq.ra * 180 / Math.PI) % 360 + 360) % 360;
        let gpLon = raDeg - gmstDegAt(d);
        gpLon = ((gpLon + 180) % 360 + 360) % 360 - 180;
        pts.push(canonicalLatLongToDisc(gpLat, gpLon, FE_RADIUS));
        latLon.push([gpLat, gpLon]);
      }
      return { pts, latLon };
    };
    const sampleFromSubPointFn = (subFn) => {
      const pts = [];
      const latLon = [];
      for (let i = 0; i <= N_GP; i++) {
        const d = new Date(dayMs + (i / N_GP) * _gpSpanMs);
        const sub = subFn(d);
        pts.push(canonicalLatLongToDisc(sub.lat, sub.lon, FE_RADIUS));
        latLon.push([sub.lat, sub.lon]);
      }
      return { pts, latLon };
    };

    // Single master toggle — `ShowGPPath` (lives in Tracker Options).
    // Traces are drawn only for bodies currently in TrackerTargets
    // (plus FollowTarget), so the disc doesn't fill with every star
    // circle when the user just wants to see a handful of paths.
    if (s.ShowGPPath) {
      const activeEph = bodySource === 'geocentric'   ? ephGeo
                      : bodySource === 'ptolemy'      ? ephPtol
                      : bodySource === 'vsop87'       ? ephVsop
                      :                                 ephApix;
      const trackerTargetArr = Array.isArray(s.TrackerTargets) ? s.TrackerTargets : [];
      const gpSet = new Set(trackerTargetArr);
      if (s.FollowTarget) gpSet.add(s.FollowTarget);

      const PLANET_COLORS = {
        sun: 0xffc844, moon: 0xf4f4f4,
        mercury: 0xd0b090, venus: 0xfff0c8, mars: 0xd05040,
        jupiter: 0xffa060, saturn: 0xe4c888,
        uranus: 0xa8d8e0, neptune: 0x7fa6e8,
      };
      for (const [body, color] of Object.entries(PLANET_COLORS)) {
        if (!gpSet.has(body)) continue;
        const sampled = sampleFrom((d) => {
          try { return activeEph.bodyGeocentric(body, d); } catch { return null; }
        });
        if (sampled) {
          c.GPPaths[`p:${body}`] = { pts: sampled.pts, latLon: sampled.latLon, color };
        }
      }

      // Stars: use `apparentStarPosition` with full apparent-of-date
      // corrections so precession + nutation + aberration produce a
      // visible declination drift across a year (~50″/yr precession,
      // 9″ nutation oscillation, 20″ aberration ellipse). Without
      // these the star's GP collapses to a single circle of constant
      // latitude regardless of how long the path span is.
      const sampleFixedStar = (raRad, decRad) =>
        sampleFrom((d) => apparentStarPosition(raRad, decRad, d, {
          precession: true, nutation: true, aberration: true,
        }));
      const starCategories = [
        [CEL_NAV_STARS,    0xffe8a0, 'cn'],
        [CATALOGUED_STARS, 0xffffff, 'cat'],
        [BLACK_HOLES,      0x9966ff, 'bh'],
        [QUASARS,          0x40e0d0, 'q'],
        [GALAXIES,         0xff80c0, 'gal'],
        [CEL_THEO_OWN,     0xff8c00, 'ct'],
      ];
      for (const [list, color, prefix] of starCategories) {
        for (const star of list) {
          if (!gpSet.has(`star:${star.id}`)) continue;
          const raRad  = (star.raH / 24) * 2 * Math.PI;
          const decRad = star.decD * Math.PI / 180;
          const sampled = sampleFixedStar(raRad, decRad);
          if (sampled) {
            c.GPPaths[`${prefix}:${star.id}`] = {
              pts: sampled.pts, latLon: sampled.latLon, color,
            };
          }
        }
      }

      for (const sat of [...SATELLITES, ...SATELLITES_EXTRA]) {
        if (!gpSet.has(`star:${sat.id}`)) continue;
        const sampled = sampleFromSubPointFn((d) => satelliteSubPoint(sat, d));
        c.GPPaths[`sat:${sat.id}`] = {
          pts: sampled.pts, latLon: sampled.latLon, color: 0x66ff88,
        };
      }
    }

    c.TrackerInfos = [];
    const targets = Array.isArray(s.TrackerTargets) ? [...s.TrackerTargets] : [];
    const followOnlyIds = new Set();
    if (s.FollowTarget && !targets.includes(s.FollowTarget)) {
      targets.push(s.FollowTarget);
      followOnlyIds.add(s.FollowTarget);
    }
    const wrapLon = (x) => ((x + 180) % 360 + 360) % 360 - 180;

    // Per-frame ephemeris loading order:
    //   • Default (Espenak / DE405) is always loaded — it's what
    //     `bodySource` resolves to upstream and what every rendered
    //     sun / moon / planet position comes from.
    //   • The four comparison pipelines (GeoC, HelioC, VSOP87,
    //     Ptolemy) only get queried below when the Tracker tab's
    //     "Ephemeris comparison" toggle (`ShowEphemerisReadings`) is
    //     on. They effectively unload from the hot path the moment
    //     the toggle flips off — NaN sentinels stand in for their
    //     readings and the tracker HUD already hides those rows
    //     when the toggle is off.
    const compareOn = !!s.ShowEphemerisReadings;
    const NAN_READING = { ra: NaN, dec: NaN };
    // Comparison-mode readings depend only on (body, date) — none of
    // the four pipelines is observer-aware. Cache the per-body
    // bundle keyed on dateMs and reuse across drag ticks. Cache is
    // rebuilt whenever the date changes (key miss).
    const _compareKey = utcDate.getTime();
    if (compareOn && (!this._compareReadingsCache || this._compareReadingsCache.key !== _compareKey)) {
      this._compareReadingsCache = { key: _compareKey, byBody: Object.create(null) };
    }
    const readingsFor = (body) => {
      if (!compareOn) {
        return { rGeo: NAN_READING, rPtol: NAN_READING, rApix: NAN_READING, rVsop: NAN_READING };
      }
      const cache = this._compareReadingsCache.byBody;
      let entry = cache[body];
      if (!entry) {
        entry = {
          rGeo:  ephGeo.bodyGeocentric(body, utcDate),
          rPtol: ephPtol.bodyGeocentric(body, utcDate),
          rApix: ephApix.bodyGeocentric(body, utcDate),
          rVsop: ephVsop.bodyGeocentric(body, utcDate),
        };
        cache[body] = entry;
      }
      return entry;
    };

    for (const target of targets) {
      let info = null;

      if (target === 'sun') {
        const { rGeo, rPtol, rApix, rVsop } = readingsFor('sun');
        info = {
          target, name: 'Sun', category: 'luminary',
          azimuth: c.SunAnglesGlobe.azimuth,
          elevation: c.SunAnglesGlobe.elevation,
          ra: c.SunRA, dec: c.SunDec,
          geoReading:        { ra: rGeo.ra,   dec: rGeo.dec   },
          ptolemyReading:    { ra: rPtol.ra,  dec: rPtol.dec  },
          astropixelsReading:{ ra: rApix.ra,  dec: rApix.dec  },
          vsop87Reading:     { ra: rVsop.ra,  dec: rVsop.dec  },
          gpLat: c.SunCelestLatLong.lat,
          gpLon: wrapLon(c.SunRA * 180 / Math.PI - c.SkyRotAngle),
          vaultCoord: c.SunVaultCoord,
          opticalVaultCoord:          c.SunOpticalVaultCoord,
          globeOpticalVaultCoord:     c.SunGlobeOpticalVaultCoord,
          opticalVaultCoordTrue:      c.SunOpticalVaultCoordTrue,
          globeOpticalVaultCoordTrue: c.SunGlobeOpticalVaultCoordTrue,
        };
      } else if (target === 'moon') {
        const { rGeo, rPtol, rApix, rVsop } = readingsFor('moon');
        info = {
          target, name: 'Moon', category: 'luminary',
          azimuth: c.MoonAnglesGlobe.azimuth,
          elevation: c.MoonAnglesGlobe.elevation,
          ra: c.MoonRA, dec: c.MoonDec,
          geoReading:        { ra: rGeo.ra,   dec: rGeo.dec   },
          ptolemyReading:    { ra: rPtol.ra,  dec: rPtol.dec  },
          astropixelsReading:{ ra: rApix.ra,  dec: rApix.dec  },
          vsop87Reading:     { ra: rVsop.ra,  dec: rVsop.dec  },
          gpLat: c.MoonCelestLatLong.lat,
          gpLon: wrapLon(c.MoonRA * 180 / Math.PI - c.SkyRotAngle),
          vaultCoord: c.MoonVaultCoord,
          opticalVaultCoord:          c.MoonOpticalVaultCoord,
          globeOpticalVaultCoord:     c.MoonGlobeOpticalVaultCoord,
          opticalVaultCoordTrue:      c.MoonOpticalVaultCoordTrue,
          globeOpticalVaultCoordTrue: c.MoonGlobeOpticalVaultCoordTrue,
        };
      } else if (PLANET_NAMES.includes(target)) {
        const p = c.Planets[target];
        if (p) {
          const { rGeo, rPtol, rApix, rVsop } = readingsFor(target);
          const gpColor = PLANET_GP_COLORS[target] || TRACKED_GP_COLORS_PLANET_DEFAULT;
          info = {
            target,
            name: target[0].toUpperCase() + target.slice(1),
            category: 'planet',
            gpColor,
            azimuth: p.anglesGlobe.azimuth,
            elevation: p.anglesGlobe.elevation,
            ra: p.ra, dec: p.dec,
            geoReading:        { ra: rGeo.ra,   dec: rGeo.dec   },
            ptolemyReading:    { ra: rPtol.ra,  dec: rPtol.dec  },
            astropixelsReading:{ ra: rApix.ra,  dec: rApix.dec  },
            vsop87Reading:     { ra: rVsop.ra,  dec: rVsop.dec  },
            gpLat: p.celestLatLong.lat,
            gpLon: wrapLon(p.ra * 180 / Math.PI - c.SkyRotAngle),
            vaultCoord: p.vaultCoord,
            opticalVaultCoord:          p.opticalVaultCoord,
            globeOpticalVaultCoord:     p.globeOpticalVaultCoord,
            opticalVaultCoordTrue:      p.opticalVaultCoordTrue,
            globeOpticalVaultCoordTrue: p.globeOpticalVaultCoordTrue,
          };
        }
      } else if (target.startsWith('star:')) {
        const starId = target.slice(5);
        let entry = c.CelNavStars.find((x) => x.id === starId);
        let def   = celNavStarById(starId);
        let cat   = 'celnav';
        if (!entry) {
          entry = c.CataloguedStars.find((x) => x.id === starId);
          def   = cataloguedStarById(starId);
          if (entry) cat = 'catalogued';
        }
        if (!entry) {
          entry = c.BlackHoles.find((x) => x.id === starId);
          def   = blackHoleById(starId);
          if (entry) cat = 'blackhole';
        }
        if (!entry) {
          entry = c.Quasars.find((x) => x.id === starId);
          def   = quasarById(starId);
          if (entry) cat = 'quasar';
        }
        if (!entry) {
          entry = c.Galaxies.find((x) => x.id === starId);
          def   = galaxyById(starId);
          if (entry) cat = 'galaxy';
        }
        if (!entry && c.CelTheoStars) {
          entry = c.CelTheoStars.find((x) => x.id === starId);
          def   = entry ? { name: entry.name, mag: entry.mag } : null;
          if (entry) cat = 'celtheo';
        }
        if (!entry) {
          entry = c.Satellites.find((x) => x.id === starId);
          def   = satelliteById(starId) || (entry ? { name: entry.name, mag: -1.5 } : null);
          if (entry) cat = 'satellite';
        }
        if (entry && def) {
          // Star RA/Dec is pipeline-independent; all five readings share it.
          const gpColorByCat = {
            celnav:     0xffe8a0,  // warm yellow
            catalogued: 0xffffff,  // white
            blackhole:  0x9966ff,  // purple
            quasar:     0x40e0d0,  // cyan
            galaxy:     0xff80c0,  // pink
            satellite:  0x66ff88,  // lime green
            celtheo:    0xff8c00,  // orange
          };
          // Stars carry a single catalog RA / Dec — no five-pipeline
          // ephemeris comparison applies. Drop the redundant
          // `*Reading` copies; the tracker HUD already gates the
          // comparison block on `info.category !== 'star'`, and the
          // tracking-info popup reads `info.ra` / `info.dec` for
          // stars (with fallbacks for sun / moon / planets that do
          // populate the per-pipeline readings).
          info = {
            target, name: def.name, category: 'star', subCategory: cat, mag: def.mag,
            gpColor: gpColorByCat[cat] || 0xffffff,
            azimuth: entry.anglesGlobe.azimuth,
            elevation: entry.anglesGlobe.elevation,
            ra:  entry.ra,
            dec: entry.dec,
            gpLat: entry.celestLatLong.lat,
            gpLon: wrapLon(entry.ra * 180 / Math.PI - c.SkyRotAngle),
            vaultCoord: entry.vaultCoord,
            opticalVaultCoord:          entry.opticalVaultCoord,
            globeOpticalVaultCoord:     entry.globeOpticalVaultCoord,
            opticalVaultCoordTrue:      entry.opticalVaultCoordTrue,
            globeOpticalVaultCoordTrue: entry.globeOpticalVaultCoordTrue,
          };
        }
      }

      if (info) {
        info.activeSource = bodySource;
        info.utcMs = utcDate.getTime();
        info._followOnly = followOnlyIds.has(target);
        // Refraction lift for the body's current true elevation, in
        // degrees. Zero when the toggle is off or the body is below
        // the horizon. The HUD uses this directly; the geocentric
        // ghost marker is gated on `_refrMode !== 'off'`.
        info.refractionDeg = refractionDeg(_refrMode, info.elevation, _refrPressure, _refrTempC);
        c.TrackerInfos.push(info);
      }
    }
  }
}
